import customIcon from "../../../../assets/images/mediaplan/Custom.png";
import demandGenerationIcon from "../../../../assets/images/mediaplan/DemandGeneration.png";
import productLaunchIcon from "../../../../assets/images/mediaplan/ProductLaunch.png";
import profitIcon from "../../../../assets/images/mediaplan/Profitability.png";
import sovIcon from "../../../../assets/images/mediaplan/ShareofVoice.png";
import sponsoredProductIcon from "../../../../assets/images/mediaplan/SponsoredProduct.png";
import sponsoredProductAutoIcon from "../../../../assets/images/mediaplan/SponsoredProductAuto.png";
import sponsoredDisplayIcon from "../../../../assets/images/mediaplan/SponsoredDisplay.png";
import sponsoredBrandIcon from "../../../../assets/images/mediaplan/SponsoredBrand.png";
import sponsoredBrandVideoIcon from "../../../../assets/images/mediaplan/SponsoredBrandVideo.png";
import autoTargetingIcon from "../../../../assets/images/mediaplan/AutoTargeting.png";
import manualTargetingIcon from "../../../../assets/images/mediaplan/ManualTargeting.png";
import funnelTargetingIcon from "../../../../assets/images/mediaplan/FunnelTargeting.png";
import { trellisPalette } from "../../../custom/analytics/palettes";
import type { TSelector } from "../../../../i18n/types";

// Shape types for the enum dictionaries below. `satisfies` clauses use these to
// give selector parameters contextual `any` typing without widening literal
// `value` types (so consumers like `AD_TYPES.SPONSORED_PRODUCT.value === 10` still
// see the precise literal).
type LabeledValue<V> = { value: V; label: TSelector };
type DescribedEntry<V> = { value: V; label: TSelector; description: TSelector; icon: string };
type AdTypeShape = {
  value: number;
  label: TSelector;
  description: TSelector;
  icon: string;
  isDisabled?: boolean;
  targetType?: string;
};
type TargetTypeShape = { value: string; label: TSelector; description: TSelector; icon: string };
type AlgorithmOptionShape = { value: string; label: TSelector; description: TSelector };
type AdStateShape = { value: number; label: TSelector; isDisabled?: boolean };
type ModerationStatusShape = { value: number; description: TSelector; label?: TSelector };
type WizardSubstep = { key: string; title: TSelector; validations: string[] };
type WizardStep = {
  key: string;
  title: TSelector;
  validations?: string[];
  substeps?: WizardSubstep[];
  defaultSubsteps?: WizardSubstep[];
  sbVideoSubsteps?: WizardSubstep[];
  landingPageSubsteps?: WizardSubstep[];
  storePageSubsteps?: WizardSubstep[];
  storeSpotlightSubsteps?: WizardSubstep[];
  autoCollectionSubsteps?: WizardSubstep[];
  funnelSubsteps?: WizardSubstep[];
};
type PlacementStep = WizardSubstep & { placementIndex: number };

export const MIN_BID_DEFAULT = 0.02;
export const MAX_BID_DEFAULT = 2000;

// Walmart constants
export const WALMART_MAX_BID_DEFAULT = 2;
export const WALMART_SB_MIN_BID_DEFAULT = 0.5;
export const WALMART_SBV_MIN_BID_DEFAULT = 0.8;

export const WALMART_TOTAL_BUDGET_DEFAULT = 1000;
export const WALMART_SELLER_SINGLE_CAMPAIGN_MIN_BUDGET = 10;
export const WALMART_SELLER_DOUBLE_CAMPAIGN_MIN_BUDGET = 26;
export const WALMART_VENDOR_SINGLE_CAMPAIGN_MIN_BUDGET = 50;
export const WALMART_VENDOR_DOUBLE_CAMPAIGN_MIN_BUDGET = 126;

export const GOALS = {
  DEMAND_GENERATION: {
    value: 5,
    label: ($) => $.advertising["Demand Generation"],
    description: ($) => $.advertising["Increase consumer awareness and demand for your products."],
    icon: demandGenerationIcon,
  },
  PRODUCT_LAUNCH: {
    value: 1,
    label: ($) => $.advertising["Product Launch"],
    description: ($) => $.advertising["Make an impact with successful new product launches."],
    icon: productLaunchIcon,
  },
  OPTIMIZE_ACOS: {
    value: 2,
    label: ($) => $.advertising["Optimize ACoS Profitability"],
    description: ($) => $.advertising["Drive profits to new heights with data-driven tools."],
    icon: profitIcon,
  },
  SOV: {
    value: 9,
    label: ($) => $.advertising["Share of Voice"],
    description: ($) =>
      $.advertising[
        "Thrive in competitive marketplaces by outranking competition with powerful, data-driven keywords."
      ],
    icon: sovIcon,
  },
  CUSTOM: {
    value: 15,
    label: ($) => $.advertising["Segmented Strategy"],
    description: ($) =>
      $.advertising[
        "Create a customized strategy that best suits your unique needs. Only recommended for experienced users."
      ],
    icon: customIcon,
  },
  IMPORTED_ADS: {
    value: 25,
    label: ($) => $.advertising["Imported Ad"],
    description: ($) => $.advertising["Imported Ad"],
    icon: customIcon,
  },
} satisfies Record<string, DescribedEntry<number>>;

export const AD_TYPES = {
  // Amazon Ad Types
  SPONSORED_PRODUCT: {
    value: 10,
    label: ($) => $.advertising["Sponsored Product"],
    description: ($) =>
      $.advertising["Display individual product listings on Amazon product pages and related shopping results."],
    icon: sponsoredProductIcon,
  },
  SPONSORED_BRAND: {
    value: 11,
    label: ($) => $.advertising["Sponsored Brand"],
    description: ($) =>
      $.advertising["Promote your brand and products in a customized ad at the top of related Amazon product pages."],
    isDisabled: false,
    icon: sponsoredBrandIcon,
  },
  SPONSORED_BRAND_VIDEO: {
    value: 12,
    label: ($) => $.advertising["Sponsored Brand: Video"],
    description: ($) => $.advertising["Engage customers with video ads that appear alongside related products."],
    isDisabled: false,
    icon: sponsoredBrandVideoIcon,
  },
  SPONSORED_PRODUCT_AUTO: {
    value: 14,
    label: ($) => $.advertising["Sponsored Product: Auto"],
    description: ($) =>
      $.advertising[
        "Amazon will automatically calculate the best time and place to display your product ad. Recommended for PPC beginners."
      ],
    icon: sponsoredProductAutoIcon,
  },
  SPONSORED_DISPLAY: {
    value: 15,
    label: ($) => $.advertising["Sponsored Display (Legacy)"],
    description: ($) =>
      $.advertising["Reach customers with informative and attractive ads that appear on relevant Amazon pages."],
    icon: sponsoredDisplayIcon,
  },
  CUSTOM: {
    value: 20,
    label: ($) => $.advertising["Custom"],
    description: ($) =>
      $.advertising[
        "Create a customized strategy that best suits your unique needs. Only recommended for experienced users."
      ],
    icon: customIcon,
  },
  SPONSORED_DISPLAY_V2: {
    value: 22,
    label: ($) => $.advertising["Sponsored Display"],
    description: ($) =>
      $.advertising["Reach customers with informative and attractive ads that appear on relevant Amazon pages."],
    icon: sponsoredDisplayIcon,
    targetType: "N/A",
  },
  SPONSORED_BRAND_V2: {
    value: 19,
    label: ($) => $.advertising["Sponsored Brand"],
    description: ($) =>
      $.advertising["Reach customers with informative and attractive ads that appear on relevant Amazon pages."],
    icon: sponsoredDisplayIcon,
  },
  SPONSORED_PRODUCT_GENERIC: {
    value: 19,
    label: ($) => $.advertising["Sponsored Product: Generic"],
    description: ($) => $.advertising["Reach a wide array of shoppers, including those looking for generic products."],
    icon: sponsoredProductIcon,
  },
  // Walmart Ad Types
  SPONSORED_PRODUCT_WALMART: {
    value: 20,
    label: ($) => $.advertising["Sponsored Product"],
    description: ($) =>
      $.advertising["Display individual product listings on Amazon product pages and related shopping results."],
    icon: sponsoredProductAutoIcon,
  },
  SPONSORED_PRODUCT_AUTO_WALMART: {
    value: 21,
    label: ($) => $.advertising["Sponsored Product: Auto"],
    description: ($) =>
      $.advertising[
        "Walmart will automatically calculate the best time and place to display your product ad. Recommended for PPC beginners."
      ],
    icon: sponsoredProductAutoIcon,
  },
  SPONSORED_BRAND_WALMART: {
    value: 23,
    label: ($) => $.advertising["Sponsored Brand"],
    description: ($) =>
      $.advertising["Promote your brand and products in a customized ad at the top of related Walmart product pages."],
    icon: sponsoredBrandIcon,
  },
  SPONSORED_BRAND_VIDEO_WALMART: {
    value: 24,
    label: ($) => $.advertising["Sponsored Brand: Video"],
    description: ($) => $.advertising["Engage customers with video ads that appear alongside related products."],
    icon: sponsoredBrandVideoIcon,
  },
  NON_TRELLIS_CAMPAIGN: {
    value: 25,
    label: ($) => $.advertising["Non Trellis Managed Campaign"],
    description: ($) => $.advertising["Third Party Campaign"],
    icon: sponsoredProductIcon,
  },
} satisfies Record<string, AdTypeShape>;

export const getMinBid = (adType?: number): number =>
  adType === AD_TYPES.SPONSORED_BRAND_WALMART.value ? WALMART_SB_MIN_BID_DEFAULT
  : adType === AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value ? WALMART_SBV_MIN_BID_DEFAULT
  : MIN_BID_DEFAULT;

export const SMB_STARTER_ALLOWED_AD_TYPES = [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value];

export const WALMART_NEGATIVE_KEYWORD_AD_TYPES: number[] = [
  AD_TYPES.SPONSORED_PRODUCT.value,
  AD_TYPES.SPONSORED_PRODUCT_WALMART.value,
  AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
];

export const SB_BRAND_AD_TYPE = {
  PRODUCT_COLLECTION: "_SB_PC_",
  STORE_SPOTLIGHT: "_SB_SS_",
  BRAND_VIDEO: "_SB_BV_",
  VIDEO: "_SB_V_",
  MANUAL_COLLECTION: "_SB_MC_",
  AUTO_COLLECTION: "_SB_AC_",
} as const;

export type SBBrandAdType = (typeof SB_BRAND_AD_TYPE)[keyof typeof SB_BRAND_AD_TYPE];

export const VIDEO_AD_TYPES = {
  PRODUCT_COLLECTION: {
    value: "productCollection",
    label: ($) => $.advertising["Product Collection"],
  },
  VIDEO: {
    value: "video",
    label: ($) => $.advertising["Product"],
  },
  BRAND_VIDEO: {
    value: "brandVideo",
    label: ($) => $.advertising["Landing Page"],
  },
} satisfies Record<string, LabeledValue<string>>;

export const AD_STATES = {
  PAUSE: { value: 0, label: ($) => $.advertising["Pause"] },
  ACTIVE: { value: 1, label: ($) => $.advertising["Active"] },
  ARCHIVE: { value: 2, label: ($) => $.advertising["Archive"], isDisabled: true },
  ENDED: { value: 5, label: ($) => $.advertising["Ended"] },
} satisfies Record<string, AdStateShape>;

// Port from app-rest-api api/gv_constants.py
export const AD_STATUSES = {
  GV_ADV_SUCCESS_STATUS: { value: 1, label: ($) => $.advertising["Success"] },
  GV_ADV_APPROVED: { value: 11, label: ($) => $.advertising["Approved"] },
  GV_ADV_IN_PROGRESS: { value: 22, label: ($) => $.advertising["In Progress"] },
  GV_ADV_REJECTED: { value: 20, label: ($) => $.advertising["Rejected"] },
  GV_ADV_CREATION_PENDING: { value: 2, label: ($) => $.advertising["Pending Creation"] },
  GV_ADV_PAUSED: { value: 3, label: ($) => $.advertising["Paused"] },
  GV_ADV_CREATION_FAILED: { value: 10, label: ($) => $.advertising["Failed"] },
  GV_ADV_ARCHIVED: { value: 4, label: ($) => $.advertising["Archived"] },
} satisfies Record<string, LabeledValue<number>>;

export const TARGET_TYPES = {
  AUTOMATIC: {
    value: "automatic",
    label: ($) => $.advertising["Automatic"],
    description: ($) =>
      $.advertising[
        "Trellis's discovery system analyzes relevant competitors and products to produce powerful keywords and ASINs."
      ],
    icon: autoTargetingIcon,
  },
  MANUAL: {
    value: "manual",
    label: ($) => $.advertising["Manual"],
    description: ($) => $.advertising["Manually add keywords and ASINs to your advertisement."],
    icon: manualTargetingIcon,
  },
  FUNNEL: {
    value: "funnel",
    label: ($) => $.advertising["Funnel"],
    description: ($) =>
      $.advertising[
        "Provide descriptions of your product attributes and automatically generate a list of keyword targets. You can also add ASIN targets manually."
      ],
    icon: funnelTargetingIcon,
  },
} satisfies Record<string, TargetTypeShape>;

export const ALGORITHM_OPTIONS = {
  PRODUCT_LAUNCH: {
    value: "v1",
    label: ($) => $.advertising["Product Launch"],
    description: ($) => $.advertising["Best for new campaigns or products with little to no performance history."],
  },
  GROWTH_MAX: {
    value: "v2",
    label: ($) => $.advertising["Growth Max"],
    description: ($) =>
      $.advertising[
        "Analyzes historical data to maximize long-term profit for each target.\n\nBest for established campaigns where stable, consistent growth is the priority."
      ],
  },
  ROAS_BOOST: {
    value: "v2.5",
    label: ($) => $.advertising["ROAS Boost"],
    description: ($) =>
      $.advertising[
        "Reacts quickly to recent performance trends, scaling up bids aggressively on high-converting keywords and pulling back on underperformers."
      ],
  },
  RULES_ENGINE_ONLY: {
    value: "rules",
    label: ($) => $.advertising["Only Rules"],
    description: ($) =>
      $.advertising["Create fully custom bidding rules tailored to your specific goals, triggers, and conditions."],
  },
  PLACEMENT_MAX: {
    value: "v3",
    label: ($) => $.advertising["Placement Max"],
    description: ($) =>
      $.advertising[
        "Optimizes bids independently for each Amazon ad placement (Top of Search, Product Pages, etc.).\n\nBest for mature campaigns with enough placement-level data and strong performance variance across placements."
      ],
  },
  SMART_SPEND: {
    value: "v4",
    label: ($) => $.advertising["Smart Spend"],
    description: ($) =>
      $.advertising[
        "Solves a global optimization across all targets in your campaign to maximize total sales within your daily budget."
      ],
  },
} as const satisfies Record<string, AlgorithmOptionShape>;

export type AlgorithmValue = (typeof ALGORITHM_OPTIONS)[keyof typeof ALGORITHM_OPTIONS]["value"];

export const SB_CAMPAIGN_GOAL_OPTIONS = {
  PAGE_VISIT: {
    value: "PAGE_VISIT",
    label: ($) => $.advertising["Page Visit (CPC)"],
  },
  BRAND_IMPRESSION_SHARE: {
    value: "BRAND_IMPRESSION_SHARE",
    label: ($) => $.advertising["Brand Impression Share (VCPM)"],
  },
} satisfies Record<string, LabeledValue<string>>;

export const BID_STRATEGY_OPTIONS = {
  UP_DOWN: { value: "AUTO_FOR_SALES", label: ($) => $.advertising["Up / Down"] },
  FIXED: { value: "manual", label: ($) => $.advertising["Fixed"] },
  DOWN_ONLY: { value: "LEGACY_FOR_SALES", label: ($) => $.advertising["Down Only"] },
  RULE_BASED: { value: "ruleBased", label: ($) => $.advertising["Rule Based"] },
} as const satisfies Record<string, LabeledValue<string>>;

export const SB_BID_STRATEGY_OPTIONS = {
  MAXIMIZE_IMMEDIATE_SALES: {
    value: "MAXIMIZE_IMMEDIATE_SALES",
    label: ($) => $.advertising["Maximize Sales"],
  },
  MAXIMIZE_NEW_TO_BRAND_CUSTOMERS: {
    value: "MAXIMIZE_NEW_TO_BRAND_CUSTOMERS",
    label: ($) => $.advertising["Maximize New-to-Brand Customers"],
  },
} satisfies Record<string, LabeledValue<string>>;

export const SD_BID_TYPE_OPTIONS = {
  VCPM: { value: "vcpm", label: ($) => $.advertising["vCPM - Pay by 1000 impressions"] },
  CONVERSIONS_CPC: { value: "cpc", label: ($) => $.advertising["Conversions CPC - Pay by click conversions"] },
  PAGE_VISITS_CPC: { value: "cpc_page_visits", label: ($) => $.advertising["Page Visits CPC - Pay by page visits"] },
} satisfies Record<string, LabeledValue<string>>;

export const SD_CAMPAIGN_TYPE_OPTIONS = {
  AUDIENCE: { value: "audience", label: ($) => $.advertising["Audience"] },
  CONTEXTUAL: { value: "contextual", label: ($) => $.advertising["Contextual"] },
} satisfies Record<string, LabeledValue<string>>;

export const SINGLE_PLAN_STEPS: WizardStep[] = [
  {
    key: "setup",
    title: ($) => $.advertising["Setup"],
    validations: ["name", "dailyBudget", "totalBudget", "maxACOS", "targetACOS", "goal", "categoryId"],
  },
  {
    key: "ad_type",
    title: ($) => $.advertising["Ad Type"],
    defaultSubsteps: [
      {
        key: "overview",
        title: ($) => $.advertising["Overview"],
        validations: ["adType", "landingPage", "dailyBudget"],
      },
    ],
    sbVideoSubsteps: [
      {
        key: "overview",
        title: ($) => $.advertising["Overview"],
        validations: ["adType", "landingPage"],
      },
      {
        key: "creative",
        title: ($) => $.advertising["Creative"],
        validations: ["video", "headline", "logo", "brand", "lifestyleImage", "creativeBrandName"],
      },
    ],
    landingPageSubsteps: [
      {
        key: "overview",
        title: ($) => $.advertising["Overview"],
        validations: ["adType", "landingPage", "brand", "categoryId"],
      },

      {
        key: "creatives",
        title: ($) => $.advertising["Creatives"],
        validations: ["brand", "video", "headline", "logo", "store", "lifestyleImage", "creativeBrandName", "page"],
      },
    ],
    storePageSubsteps: [
      {
        key: "overview",
        title: ($) => $.advertising["Overview"],
        validations: ["adType", "landingPage", "categoryId"],
      },
      {
        key: "landing_page",
        title: ($) => $.advertising["Landing Page"],
        validations: ["brand", "store", "page"],
      },
      {
        key: "creatives",
        title: ($) => $.advertising["Creatives"],
        validations: ["logo", "video", "headline", "lifestyleImage", "creativeBrandName"],
      },
    ],
    storeSpotlightSubsteps: [
      {
        key: "overview",
        title: ($) => $.advertising["Overview"],
        validations: ["adType", "landingPage", "categoryId"],
      },
      {
        key: "landing_page",
        title: ($) => $.advertising["Landing Page"],
        validations: ["brand", "store", "storeSpotlightSubpages"],
      },
      {
        key: "creatives",
        title: ($) => $.advertising["Creatives"],
        validations: ["logo", "video", "headline", "lifestyleImage", "creativeBrandName"],
      },
    ],
    autoCollectionSubsteps: [
      {
        key: "overview",
        title: ($) => $.advertising["Overview"],
        validations: ["adType", "landingPage", "brand", "categoryId"],
      },
      {
        key: "creatives",
        title: ($) => $.advertising["Creatives"],
        validations: ["brand", "logo", "creativeBrandName"],
      },
    ],
  },
  {
    key: "select_products",
    title: ($) => $.advertising["Select Products"],
    defaultSubsteps: [
      {
        key: "select_products",
        title: ($) => $.advertising["Select Products"],
        validations: ["categoryId", "product"],
      },
    ],
    landingPageSubsteps: [
      {
        key: "landing_page",
        title: ($) => $.advertising["Landing Page"],
        validations: ["landingPageProduct", "creativeProduct", "product"],
      },
    ],
    autoCollectionSubsteps: [
      {
        key: "select_products",
        title: ($) => $.advertising["Product Exclusions"],
        validations: ["excludedProducts"],
      },
    ],
  },
  {
    key: "targeting",
    title: ($) => $.advertising["Targeting"],
    defaultSubsteps: [
      {
        key: "targeting",
        title: ($) => $.advertising["Targeting"],
        validations: [
          "targetType",
          "filledTags",
          "filledAsins",
          "filledTagsOrAsins",
          "keywords",
          "filledProductBrands",
          "filledProductKeys",
          "sdCampaignType",
          "sdBidType",
          "sdLookbackPeriod",
          "sdAudienceTargets",
          "sdContextualTargets",
          "sdAdditionalCategoryTargets",
          "validatingCampaignNames",
          "duplicateCampaignNames",
          "campaignNames",
        ],
      },
    ],
    funnelSubsteps: [
      {
        key: "targeting",
        title: ($) => $.advertising["Targeting"],
        validations: [
          "targetType",
          "filledTags",
          "filledAsins",
          "filledTagsOrAsins",
          "keywords",
          "filledProductBrands",
          "filledProductKeys",
          "sdCampaignType",
          "sdBidType",
          "sdLookbackPeriod",
          "sdAudienceTargets",
          "sdContextualTargets",
          "sdAdditionalCategoryTargets",
          "validatingCampaignNames",
          "duplicateCampaignNames",
          "campaignNames",
        ],
      },
      {
        key: "advanced",
        title: ($) => $.advertising["Advanced"],
        validations: [
          "filledNegativeKeywords",
          "filledNegativeAsins",
          "homePlacementBidMultiplier",
          "pagePlacementBidMultiplier",
          "otherPlacementBidMultiplier",
          "shopperSegmentBidMultiplier",
          "topOfSearchPlacementBidMultiplier",
          "topOfSearchBidMultiplier",
          "productPageMultiplier",
          "restOfSearchMultiplier",
          "amazonBusinessMultiplier",
          "audienceBidMultiplier",
          "existingBiddingProgram",
          "costControlTargetCPC",
        ],
      },
    ],
  },
  {
    key: "review",
    title: ($) => $.advertising["Review"],
    validations: ["name", "dailyBudget", "maxACOS", "targetACOS"],
  },
];

export const WALMART_CREATIVES_STEP: PlacementStep = {
  key: "creatives",
  title: ($) => $.advertising["Creatives"],
  validations: ["walmartBrandName", "walmartHeadline", "walmartClickURL", "walmartImage"],
  placementIndex: 3,
};

export const SPONSORED_DISPLAY_CREATIVES_STEP: PlacementStep = {
  key: "creatives",
  title: ($) => $.advertising["Creatives"],
  validations: ["logo", "video", "headline", "lifestyleImage", "customSdStorePageEnabled"],
  placementIndex: 3,
};

// Bidding rule validations happen internally using react-hook-form
export const BIDDING_RULES_SUBSTEP: WizardSubstep = {
  key: "bidding_program",
  title: ($) => $.advertising["Bidding Program"],
  validations: ["newBiddingProgram"],
};

export const COMPOSITE_PLAN_STEPS: WizardStep[] = [
  {
    key: "setup",
    title: ($) => $.advertising["Setup"],
    validations: ["name"],
  },
  {
    key: "select_products",
    title: ($) => $.advertising["Select Products"],
    validations: ["categoryId", "product"],
  },
  {
    key: "tactics",
    title: ($) => $.advertising["Tactics"],
    validations: [
      "validatingCampaignNames",
      "duplicateCampaignNames",
      "duplicateSegmentedCampaignNames",
      "campaignNames",
    ],
  },
  {
    key: "targeting",
    title: ($) => $.advertising["Targeting"],
    validations: [
      "targetType",
      "filledTags",
      "filledAsins",
      "filledTagsOrAsins",
      "keywords",
      "filledProductBrands",
      "filledCompetitorBrands",
      "filledProductKeys",
      "brandTargetCollision",
      "sdCampaignType",
      "sdBidType",
      "sdLookbackPeriod",
      "sdAudienceTargets",
      "sdContextualTargets",
      "sdAdditionalCategoryTargets",
    ],
  },
  { key: "review", title: ($) => $.advertising["Review"], validations: [] },
];

export const INVENTORY_STATES: Record<string, { value: string; label: TSelector; description: TSelector }> = {
  OVERSTOCK: {
    value: "OVERSTOCK",
    label: ($) => $.advertising["Overstock"],
    description: ($) => $.advertising["Product is overstock."],
  },
  LOW_STOCK: {
    value: "LOW_STOCK",
    label: ($) => $.advertising["Low Stock"],
    description: ($) => $.advertising["Product is stock is running low."],
  },
  IN_STOCK_SCARCE: {
    value: "IN_STOCK_SCARCE",
    label: ($) => $.advertising["Low Stock"],
    description: ($) => $.advertising["Product is stock is running low."],
  },
  OUT_OF_STOCK: {
    value: "OUT_OF_STOCK",
    label: ($) => $.advertising["Out of Stock"],
    description: ($) => $.advertising["Product is out of stock."],
  },
  IN_STOCK: {
    value: "IN_STOCK",
    label: ($) => $.advertising["In Stock"],
    description: ($) => $.advertising["Product is in stock."],
  },
};

export const SALE_STATES: Record<string, { value: string; label: TSelector; description: TSelector }> = {
  BEST_SELLER: {
    value: "BEST_SELLER",
    label: ($) => $.advertising["Best Seller"],
    description: ($) => $.advertising["Product is a best seller."],
  },
  CLEARANCE: {
    value: "CLEARANCE",
    label: ($) => $.advertising["Clearance"],
    description: ($) => $.advertising["Product is on clearance."],
  },
  NORMAL: {
    value: "NORMAL",
    label: ($) => $.advertising["Growth"],
    description: ($) => $.advertising["Product is not on sale."],
  },
  NEW_PRODUCT: {
    value: "NEW_PRODUCT",
    label: ($) => $.advertising["New Product"],
    description: ($) => $.advertising["Product is new."],
  },
  INACTIVE: {
    value: "INACTIVE",
    label: ($) => $.advertising["Inactive"],
    description: ($) => $.advertising["Product is inactive."],
  },
};

export const MODERATION_STATUSES = {
  FAILED: {
    value: 20,
    description: ($) => $.advertising["Moderation rejected advertisement"],
    label: ($) => $.advertising["Failed"],
  },
  APPROVED: {
    value: 11,
    description: ($) => $.advertising["Moderation approved advertisement"],
  },
  PENDING: {
    value: 22,
    description: ($) => $.advertising["Moderation pending for advertisement"],
    label: ($) => $.advertising["Pending"],
  },
} satisfies Record<string, ModerationStatusShape>;

export const AD_CREATION_FAILED_STATUS = 10;

// Creatives validations - used for tagging asset cards with eligibility status by ad type
export const CREATIVE_VALIDATIONS = {
  [AD_TYPES.SPONSORED_DISPLAY_V2.value]: {
    squareImage: { minHeight: 628, minWidth: 628 },
    lifestyleImage: { minHeight: 628, minWidth: 1200, maxFileSize: 5000000, aspectRatio: 1.91 },
    logo: { minHeight: 100, minWidth: 600, maxFileSize: 1000000 },
  },
  [AD_TYPES.SPONSORED_BRAND.value]: {
    squareImage: {},
    lifestyleImage: { minHeight: 628, minWidth: 1200, maxFileSize: 5000000 },
    logo: { minHeight: 400, minWidth: 400, maxFileSize: 1000000 },
  },
  [AD_TYPES.SPONSORED_BRAND_V2.value]: {
    squareImage: {},
    lifestyleImage: { minHeight: 628, minWidth: 1200, maxFileSize: 5000000 },
    logo: { minHeight: 400, minWidth: 400, maxFileSize: 1000000 },
  },
  [AD_TYPES.SPONSORED_BRAND_VIDEO.value]: {
    squareImage: {},
    lifestyleImage: {},
    logo: { minHeight: 400, minWidth: 400, maxFileSize: 1000000 },
  },
};

export const PRODUCT_AD_PAUSED = 3;
export const PRODUCT_AD_ENABLED = 1;

export const MONTHLY_BUDGET_MULTIPLIER = 30;

export const MAX_ACOS_NEGATION_THESHOLD_DEFAULT = 150;
export const CUMULATIVE_NEGATION_THESHOLD_DEFAULT = 125;

// Amazon SB constants
export const SB_CAMPAIGN_GOAL_SUPPORTED_MARKETPLACES = ["US", "USA", "CA", "UK", "DE", "FR", "IT", "ES", "IN", "JP"];
export const MIN_SB_TARGET_CPC = 0.5;
export const DEFAULT_SB_TARGET_CPC = 1;
export const SP_SPOTLIGHT_VIDEO_SUPPORTED_MARKETPLACES = ["USA"];

export const AD_TYPE_COLOR_MAP = {
  // Amazon Ad Types
  [AD_TYPES.SPONSORED_PRODUCT.value]: trellisPalette[3],
  [AD_TYPES.SPONSORED_PRODUCT_AUTO.value]: trellisPalette[2],
  [AD_TYPES.SPONSORED_BRAND.value]: trellisPalette[1],
  [AD_TYPES.SPONSORED_BRAND_V2.value]: trellisPalette[1],
  [AD_TYPES.SPONSORED_BRAND_VIDEO.value]: trellisPalette[14],
  [AD_TYPES.SPONSORED_DISPLAY.value]: trellisPalette[5],
  [AD_TYPES.SPONSORED_DISPLAY_V2.value]: trellisPalette[5],
  // Walmart Ad Types
  [AD_TYPES.SPONSORED_PRODUCT_WALMART.value]: trellisPalette[3],
  [AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value]: trellisPalette[2],
  [AD_TYPES.SPONSORED_BRAND_WALMART.value]: trellisPalette[1],
  [AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value]: trellisPalette[14],
};

export const AMAZON_BUSINESS_SITE_RESTRICTION = "AMAZON_BUSINESS";

export const AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES = [
  "USA",
  "CA",
  "MX",
  "DE",
  "UK",
  "FR",
  "IT",
  "ES",
  "IN",
  "JP",
];

export const QUERY_STAGE_COLOR_MAP = {
  awareness: trellisPalette[4],
  consideration: trellisPalette[3],
  loyalty: trellisPalette[5],
  conversion: trellisPalette[2],
  unknown: trellisPalette[6],
};

// Amazon audienceSegmentType values sent alongside a shopper cohort bidding audience segment.
// Custom-built audiences are split by the trailing segment of the targetable entity's path:
// "Product" audiences are behavioral, "AMC" audiences come from Amazon Marketing Cloud.
export const AUDIENCE_SEGMENT_TYPES = {
  SPONSORED_ADS_AMC: "SPONSORED_ADS_AMC",
  BEHAVIOR_DYNAMIC: "BEHAVIOR_DYNAMIC",
} as const;

export type AudienceSegmentType = (typeof AUDIENCE_SEGMENT_TYPES)[keyof typeof AUDIENCE_SEGMENT_TYPES];

export const AUDIENCE_SEGMENT_TYPE_BY_ENTITY_PATH: Record<string, AudienceSegmentType> = {
  "audience category/custom-built/product": AUDIENCE_SEGMENT_TYPES.BEHAVIOR_DYNAMIC,
  "audience category/custom-built/amc": AUDIENCE_SEGMENT_TYPES.SPONSORED_ADS_AMC,
};
